<script>
    import { createEventDispatcher } from "svelte";
    import {
        supplyPlanSearch,
        fetchSupplyPlanFromDB,
        isDataLoading,
        itemGroupTree,
        fetchItemGroupTree
    } from "./store.js";
    import { get } from "svelte/store";
    import { fade, scale } from "svelte/transition";
    import toast from "svelte-french-toast";

    export let isOpen = false;
    const dispatch = createEventDispatcher();

    // UVW_ITEM_GROUP_UNITY 데이터(제품유형: MLSFC, 품목그룹: ITEM_GROUP, 기종: MHRLS_KND) 기반 3단계 동적 트리
    let treeData = [];
    let isTreeLoading = false;

    function buildTree(rows) {
        if (!rows || rows.length === 0) return [];

        // 제품유형 > 품목그룹 > 기종 계층으로 그룹핑 (조회 순서 유지)
        const ptMap = new Map();
        rows.forEach(r => {
            const ptCode = String(r.mlsfc_code || "").trim();
            const grpCode = String(r.item_group_code || "").trim();
            const mdlCode = String(r.mhrls_knd_code || "").trim();
            if (!grpCode) return;

            if (!ptMap.has(ptCode)) {
                ptMap.set(ptCode, { code: ptCode, name: String(r.mlsfc_nm || "").trim(), groups: new Map() });
            }
            const pt = ptMap.get(ptCode);
            if (!pt.groups.has(grpCode)) {
                pt.groups.set(grpCode, { code: grpCode, name: String(r.item_group_nm || "").trim(), models: new Set() });
            }
            if (mdlCode) pt.groups.get(grpCode).models.add(mdlCode);
        });

        const ptNodes = [...ptMap.values()].map(pt => {
            const children = [...pt.groups.values()].map(grp => ({
                id: `GRP_${pt.code}|${grp.code}`,
                code: grp.code,
                name: grp.name,
                label: `${grp.name || grp.code}`,
                type: "itemGroup",
                checked: false,
                indeterminate: false,
                expanded: false,
                children: [...grp.models].map(mdl => ({
                    id: `MDL_${pt.code}|${grp.code}|${mdl}`,
                    code: mdl,
                    groupCode: grp.code,
                    name: mdl,
                    label: `${mdl}`,
                    type: "model",
                    checked: false,
                    indeterminate: false,
                    children: []
                }))
            }));

            return {
                id: `PT_${pt.code}`,
                code: pt.code,
                name: pt.name,
                label: `${pt.name || pt.code || "미분류"}`,
                type: "productType",
                expanded: true,
                checked: false,
                indeterminate: false,
                children
            };
        });

        return [
            {
                id: "ROOT",
                name: "ALL",
                code: "",
                label: "ALL (전체 제품)",
                type: "root",
                expanded: true,
                checked: false,
                indeterminate: false,
                children: ptNodes
            }
        ];
    }

    // 하위 노드 상태로부터 상위 노드의 체크/부분체크 상태를 재계산 (0: 없음, 1: 일부, 2: 전체)
    function recompute(node) {
        if (!node.children || node.children.length === 0) {
            node.indeterminate = false;
            return node.checked ? 2 : 0;
        }
        let all = true;
        let any = false;
        node.children.forEach(child => {
            const s = recompute(child);
            if (s !== 2) all = false;
            if (s !== 0) any = true;
        });
        node.checked = all;
        node.indeterminate = any && !all;
        return all ? 2 : any ? 1 : 0;
    }

    function refreshTree() {
        if (!treeData || treeData.length === 0) return;
        recompute(treeData[0]);
        treeData = [...treeData];
    }

    // 하위 자식 노드 재귀 체크 설정
    function setCheckRecursive(node, checked) {
        node.checked = checked;
        node.indeterminate = false;
        if (node.children && node.children.length > 0) {
            node.children.forEach(child => setCheckRecursive(child, checked));
        }
    }

    // 노드 클릭 토글 (ALL / 제품유형 / 품목그룹 / 기종 공통)
    function toggleNode(node) {
        const nextState = !node.checked;
        setCheckRecursive(node, nextState);
        refreshTree();
    }

    // 펼침/접힘 토글
    function toggleExpand(node) {
        node.expanded = !node.expanded;
        treeData = [...treeData];
    }

    // 모두 열기: 제품유형 + 품목그룹 하위(기종)까지 전부 펼침
    function expandAll() {
        if (!treeData || !treeData[0]) return;
        treeData[0].expanded = true;
        treeData[0].children.forEach(pt => {
            pt.expanded = true;
            pt.children.forEach(grp => {
                grp.expanded = true;
            });
        });
        treeData = [...treeData];
    }

    // 모두 닫기: 제품유형(환경장비, 제설장비 등)까지만 보이도록 접음
    function collapseAll() {
        if (!treeData || !treeData[0]) return;
        treeData[0].expanded = true;
        treeData[0].children.forEach(pt => {
            pt.expanded = false;
            pt.children.forEach(grp => {
                grp.expanded = false;
            });
        });
        treeData = [...treeData];
    }

    // 전체 선택 (모두 체크)
    function checkAll() {
        if (treeData && treeData[0]) {
            setCheckRecursive(treeData[0], true);
            refreshTree();
        }
    }

    // 선택 초기화 (모두 해제)
    function uncheckAll() {
        if (treeData && treeData[0]) {
            setCheckRecursive(treeData[0], false);
            refreshTree();
        }
    }

    // 조회 후 다시 열었을 때: 체크(전체/일부)된 품목그룹은 기종(소분류)까지 펼쳐서 선택 상태를 바로 확인할 수 있게 함
    function expandPartialGroups() {
        if (!treeData || !treeData[0]) return;
        treeData[0].expanded = true;
        treeData[0].children.forEach(pt => {
            if (pt.checked || pt.indeterminate) pt.expanded = true;
            pt.children.forEach(grp => {
                if (grp.checked || grp.indeterminate) {
                    grp.expanded = true;
                    pt.expanded = true;
                }
            });
        });
        treeData = [...treeData];
    }

    let wasOpen = false;

    // 모달이 열리는 순간(false -> true)에만 트리를 초기화하고 검색 조건을 동기화
    $: if (isOpen && !wasOpen) {
        wasOpen = true;
        initModalTree();
    } else if (!isOpen && wasOpen) {
        wasOpen = false;
    }

    async function initModalTree() {
        let rows = get(itemGroupTree);
        if (!rows || rows.length === 0) {
            isTreeLoading = true;
            rows = await fetchItemGroupTree();
            isTreeLoading = false;
        }
        treeData = buildTree(rows);
        syncChecksFromSearch();
    }

    function syncChecksFromSearch() {
        if (!treeData || treeData.length === 0) return;
        const search = get(supplyPlanSearch);
        const selTreeIds = search.selectedTreeIds || [];
        const selGrps = search.selectedItemGroups || [];
        const selPairs = search.selectedGroupModels || [];
        const selPts = search.selectedProductTypes || [];
        const pt = search.productType;
        const grp = search.itemGroup;

        const root = treeData[0];

        // 1) 상단 검색바의 select 박스에서 특정 제품유형 또는 품목그룹이 선택되어 있는 경우 (최우선 실시간 동기화)
        const hasDirectPt = Boolean(pt && pt !== "ALL" && pt !== "");
        const hasDirectGrp = Boolean(grp && grp !== "ALL" && grp !== "");

        // 트리에서 직접 선택한 이력이 있으면 그 값이 우선 (상단 select 는 단일값만 담을 수 있어 부분 선택을 표현 못 함)
        const hasTreeSel = selTreeIds.length > 0 && !selTreeIds.includes("ROOT") && !selTreeIds.includes("ALL");

        if (!hasTreeSel && (hasDirectPt || hasDirectGrp)) {
            root.children.forEach(pNode => {
                const matchPt = hasDirectPt ? (pNode.code === pt || pNode.name === pt) : true;
                pNode.children.forEach(gNode => {
                    // 품목그룹까지 지정된 경우: 해당 제품유형의 해당 품목그룹만 체크 / 제품유형만 지정된 경우: 하위 전체 체크
                    const matchGrp = hasDirectGrp
                        ? matchPt && (gNode.code === grp || gNode.name === grp)
                        : matchPt;
                    setCheckRecursive(gNode, Boolean(matchGrp));
                });
            });
            refreshTree();
            expandPartialGroups();
            return;
        }

        // 2) 트리에서 선택했던 고유 ID 목록이 있는 경우 복원 (ROOT/ALL 단독 제외)
        if (selTreeIds.length > 0 && !selTreeIds.includes("ROOT") && !selTreeIds.includes("ALL")) {
            const idSet = new Set(selTreeIds.map(id => String(id).trim()));
            (function apply(node) {
                node.checked = idSet.has(node.id);
                if (node.children) node.children.forEach(apply);
            })(root);
            refreshTree();
            expandPartialGroups();
            return;
        }

        // 3) 다중 선택 배열이 있는 경우 (제품유형 / 품목그룹 전체 / 일부 기종 쌍)
        if (selPts.length > 0 || selGrps.length > 0 || selPairs.length > 0) {
            const ptSet = new Set(selPts.map(String));
            const grpSet = new Set(selGrps.map(String));
            const pairSet = new Set(selPairs.map(String));
            const hasNarrow = grpSet.size > 0 || pairSet.size > 0;
            root.children.forEach(pNode => {
                const isPtSelected = ptSet.has(pNode.code) || ptSet.has(pNode.name);
                pNode.children.forEach(gNode => {
                    const isGrpFull = grpSet.has(gNode.code) || grpSet.has(gNode.name) || (isPtSelected && !hasNarrow);
                    if (isGrpFull) {
                        setCheckRecursive(gNode, true);
                    } else {
                        setCheckRecursive(gNode, false);
                        gNode.children.forEach(mNode => {
                            mNode.checked = pairSet.has(`${gNode.code}|${mNode.code}`);
                        });
                    }
                });
            });
            refreshTree();
            expandPartialGroups();
            return;
        }

        // 4) 그 외 기본 전체 선택 상태
        checkAll();
    }

    // 모달 닫기
    function handleClose() {
        dispatch("close");
    }

    // [적용 및 조회] 버튼 클릭
    async function handleApplyAndSearch() {
        const root = treeData[0];
        if (!root) return;

        if (root.checked) {
            // 전체 선택된 경우 -> 조건 초기화 (전체 조회) 및 selectedTreeIds에 ROOT 보존
            supplyPlanSearch.update(s => ({
                ...s,
                productType: "",
                itemGroup: "",
                model: "",
                selectedItemGroups: [],
                selectedProductTypes: [],
                selectedModels: [],
                selectedGroupModels: [],
                selectedTreeIds: ["ROOT"],
            }));
        } else {
            // 체크된 모든 노드의 고유 ID 수집
            const checkedTreeIds = [];
            (function collectNodeIds(node) {
                if (node.checked) checkedTreeIds.push(node.id);
                if (node.children && node.children.length > 0) node.children.forEach(collectNodeIds);
            })(root);

            // 아무것도 선택하지 않은 경우 경고
            if (checkedTreeIds.length === 0 && !root.indeterminate) {
                toast.error("최소 1개 이상의 분류를 선택하거나 전체를 선택해주세요.");
                return;
            }

            const selPtCodes = [];
            const fullGrpCodes = [];   // 하위 기종 전체가 선택된 품목그룹
            const pairs = [];          // 일부 기종만 선택된 품목그룹의 "품목그룹|기종"
            const touchedGrpCodes = new Set();

            root.children.forEach(pt => {
                let anyChecked = false;
                pt.children.forEach(grp => {
                    if (grp.checked) {
                        fullGrpCodes.push(grp.code);
                        touchedGrpCodes.add(grp.code);
                        anyChecked = true;
                    } else if (grp.indeterminate) {
                        grp.children.forEach(mdl => {
                            if (mdl.checked) pairs.push(`${grp.code}|${mdl.code}`);
                        });
                        touchedGrpCodes.add(grp.code);
                        anyChecked = true;
                    }
                });
                if ((pt.checked || anyChecked) && pt.code) selPtCodes.push(pt.code);
            });

            if (fullGrpCodes.length === 0 && pairs.length === 0) {
                toast.error("최소 1개 이상의 분류를 선택하거나 전체를 선택해주세요.");
                return;
            }

            supplyPlanSearch.update(s => {
                const updated = { ...s };
                updated.selectedTreeIds = checkedTreeIds;
                updated.selectedItemGroups = fullGrpCodes;
                updated.selectedGroupModels = pairs;
                updated.selectedProductTypes = selPtCodes;

                // 제품유형
                updated.productType = selPtCodes.length === 1 ? selPtCodes[0] : "";

                // 품목그룹 (전체/일부 선택 합쳐서 1개일 때만 상단 select 에 반영)
                updated.itemGroup = touchedGrpCodes.size === 1 ? [...touchedGrpCodes][0] : "";

                updated.model = "";
                updated.selectedModels = [];

                return updated;
            });
        }

        // DB 즉시 조회 실행
        try {
            await fetchSupplyPlanFromDB(get(supplyPlanSearch));
            toast.success("선택한 조건으로 공급계획을 조회했습니다.", { duration: 1500 });
        } catch (e) {
            console.error("트리 조건 조회 오류:", e);
            toast.error("조회 중 오류가 발생했습니다.");
        }

        dispatch("close");
    }

    // 선택된 항목 개수 카운트 (전체 선택된 품목그룹은 1건, 일부 선택된 품목그룹은 선택된 기종 수만큼)
    $: selectedCount = (() => {
        if (!treeData || !treeData[0]) return 0;
        if (treeData[0].checked) return "전체";
        let count = 0;
        treeData[0].children.forEach(pt => {
            pt.children.forEach(grp => {
                if (grp.checked) count++;
                else if (grp.indeterminate) count += grp.children.filter(m => m.checked).length;
            });
        });
        return count;
    })();
</script>

{#if isOpen}
    <div class="modal-backdrop" on:click={handleClose} transition:fade={{ duration: 180 }}>
        <div class="modal-box" on:click|stopPropagation transition:scale={{ duration: 200, start: 0.95 }}>
            <!-- 모달 헤더: UVW_ITEM_GROUP_UNITY 제품유형 ➔ 품목그룹 ➔ 기종 (3단계) -->
            <div class="modal-header">
                <div class="modal-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                        <polyline points="2 17 12 22 22 17"/>
                        <polyline points="2 12 12 17 22 12"/>
                    </svg>
                    <h3>제품 분류 트리 선택</h3>
                    <span class="tree-sub-desc">(제품유형 ➔ 품목그룹 ➔ 기종)</span>
                </div>
                <button class="close-btn" on:click={handleClose} title="닫기">✕</button>
            </div>

            <!-- 모달 바디: 3단계 제품 트리 -->
            <div class="modal-body">
                {#if isTreeLoading}
                    <div class="tree-empty">제품 분류를 불러오는 중입니다...</div>
                {:else if treeData.length === 0}
                    <div class="tree-empty">조회된 제품 분류가 없습니다.</div>
                {/if}
                <div class="tree-container">
                    {#each treeData as root}
                        <div class="tree-root">
                            <!-- 루트 노드: ALL (전체 제품) -->
                            <div
                                class="tree-node root-node"
                                class:selected={root.checked}
                                on:click={() => toggleNode(root)}
                            >
                                <div
                                    class="custom-checkbox"
                                    class:checked={root.checked}
                                    class:indeterminate={root.indeterminate}
                                ></div>
                                <span class="tree-icon">📂</span>
                                <span class="tree-label bold">{root.label}</span>
                            </div>

                            <div class="tree-children">
                                {#each root.children as pt}
                                    <div class="tree-cat-group">
                                        <!-- 1단계: 제품유형 (환경장비, 제설장비 등) -->
                                        <div
                                            class="tree-node cat-node"
                                            class:selected={pt.checked}
                                            on:click={() => toggleNode(pt)}
                                        >
                                            {#if pt.children.length > 0}
                                                <button type="button" class="expand-btn" on:click|stopPropagation={() => toggleExpand(pt)} title={pt.expanded ? "접기" : "펼치기"}>
                                                    {pt.expanded ? "▾" : "▸"}
                                                </button>
                                            {:else}
                                                <span class="expand-spacer"></span>
                                            {/if}
                                            <div
                                                class="custom-checkbox"
                                                class:checked={pt.checked}
                                                class:indeterminate={pt.indeterminate}
                                            ></div>
                                            <span class="tree-icon">📁</span>
                                            <span class="tree-label">{pt.label}</span>
                                        </div>

                                        {#if pt.expanded}
                                            <div class="tree-children">
                                                {#each pt.children as grp}
                                                    <!-- 2단계: 품목그룹 (노면차, 살수차, 분진차 등) -->
                                                    <div
                                                        class="tree-node grp-node"
                                                        class:selected={grp.checked}
                                                        on:click={() => toggleNode(grp)}
                                                    >
                                                        {#if grp.children.length > 0}
                                                            <button type="button" class="expand-btn" on:click|stopPropagation={() => toggleExpand(grp)} title={grp.expanded ? "기종 접기" : "기종 펼치기"}>
                                                                {grp.expanded ? "▾" : "▸"}
                                                            </button>
                                                        {:else}
                                                            <span class="expand-spacer"></span>
                                                        {/if}
                                                        <div
                                                            class="custom-checkbox"
                                                            class:checked={grp.checked}
                                                            class:indeterminate={grp.indeterminate}
                                                        ></div>
                                                        <span class="tree-icon">📦</span>
                                                        <span class="tree-label">{grp.label}</span>
                                                        {#if grp.children.length > 0}
                                                            <span class="child-count">{grp.children.length}</span>
                                                        {/if}
                                                    </div>

                                                    {#if grp.expanded && grp.children.length > 0}
                                                        <div class="tree-children">
                                                            {#each grp.children as mdl}
                                                                <!-- 3단계: 기종 (1톤, 8.5톤, 15톤_N 등) -->
                                                                <div
                                                                    class="tree-node mdl-node"
                                                                    class:selected={mdl.checked}
                                                                    on:click={() => toggleNode(mdl)}
                                                                >
                                                                    <div
                                                                        class="custom-checkbox"
                                                                        class:checked={mdl.checked}
                                                                    ></div>
                                                                    <span class="tree-icon">🚜</span>
                                                                    <span class="tree-label">{mdl.label}</span>
                                                                </div>
                                                            {/each}
                                                        </div>
                                                    {/if}
                                                {/each}
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            </div>
                        </div>
                    {/each}
                </div>
            </div>

            <!-- 모달 푸터: 선택초기화, 닫기, [적용 및 조회] 버튼 -->
            <div class="modal-footer">
                <div class="footer-left">
                    <button type="button" class="tool-btn" on:click={expandAll} title="모든 분류 펼치기">▾ 모두 열기</button>
                    <button type="button" class="tool-btn" on:click={collapseAll} title="제품유형까지만 보이도록 접기">▸ 모두 닫기</button>
                    {#if selectedCount !== 0}
                        <span class="selection-badge">선택됨: <strong>{selectedCount}</strong>건</span>
                    {/if}
                </div>
                <div class="footer-right">
                    <button type="button" class="btn btn-secondary" on:click={handleClose}>
                        닫기
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        on:click={handleApplyAndSearch}
                        disabled={$isDataLoading}
                    >
                        {#if $isDataLoading}
                            조회 중...
                        {:else}
                            🔍 적용 및 조회
                        {/if}
                    </button>
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.65);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        padding: 16px;
    }
    .modal-box {
        background: white;
        width: 100%;
        max-width: 640px;
        border-radius: 12px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border: 1px solid #cbd5e1;
    }
    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 20px;
        border-bottom: 1px solid #e2e8f0;
        background: #f8fafc;
    }
    .modal-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #0f172a;
    }
    .modal-title svg {
        color: #2563eb;
    }
    .modal-title h3 {
        margin: 0;
        font-size: 1rem;
        font-weight: 800;
        letter-spacing: -0.02em;
    }
    .tree-sub-desc {
        font-size: 0.78rem;
        color: #64748b;
        font-weight: 600;
    }
    .close-btn {
        background: none;
        border: none;
        font-size: 1.15rem;
        color: #94a3b8;
        cursor: pointer;
        padding: 2px 6px;
        line-height: 1;
        border-radius: 4px;
        transition: all 0.15s;
    }
    .close-btn:hover {
        color: #0f172a;
        background: #e2e8f0;
    }
    .modal-body {
        padding: 8px 14px;
        height: min(86vh, 900px);
        overflow-y: auto;
    }
    .tree-empty {
        padding: 18px 0;
        text-align: center;
        font-size: 0.85rem;
        color: #64748b;
        font-weight: 600;
    }
    .tool-btn {
        padding: 6px 12px;
        font-size: 0.82rem;
        font-weight: 700;
        color: #475569;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.15s;
    }
    .tool-btn:hover {
        background: #eff6ff;
        border-color: #93c5fd;
        color: #1d4ed8;
    }
    .tree-container {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .tree-node {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 2px 8px;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.15s;
        user-select: none;
    }
    .tree-node:hover {
        background: #f1f5f9;
    }
    .root-node {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        font-size: 0.85rem;
        margin-bottom: 2px;
    }
    .root-node:hover {
        background: #eff6ff;
        border-color: #bfdbfe;
    }
    .cat-node {
        font-weight: 700;
        color: #1e293b;
        font-size: 0.82rem;
    }
    .cat-node:hover {
        background: #eff6ff;
        color: #1d4ed8;
    }
    .grp-node {
        font-size: 0.78rem;
        font-weight: 600;
        color: #334155;
    }
    .grp-node:hover {
        background: #eff6ff;
        color: #2563eb;
    }
    .mdl-node {
        font-size: 0.75rem;
        font-weight: 500;
        color: #475569;
        padding: 1px 8px;
    }
    .mdl-node:hover {
        background: #eff6ff;
        color: #2563eb;
    }
    .tree-children {
        padding-left: 14px;
        display: flex;
        flex-direction: column;
        gap: 1px;
        border-left: 1px dashed #cbd5e1;
        margin-left: 10px;
        margin-top: 1px;
        margin-bottom: 2px;
    }

    /* 펼침/접힘 버튼 */
    .expand-btn {
        flex-shrink: 0;
        width: 22px;
        height: 20px;
        padding: 0;
        margin-right: 4px;
        border: 1px solid #cbd5e1;
        background: #f1f5f9;
        color: #475569;
        font-size: 0.8rem;
        line-height: 1;
        cursor: pointer;
        border-radius: 5px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s;
    }
    .expand-btn:hover {
        background: #dbeafe;
        border-color: #60a5fa;
        color: #1d4ed8;
    }
    .expand-btn:active {
        transform: scale(0.94);
    }
    .expand-spacer {
        flex-shrink: 0;
        width: 22px;
        height: 20px;
        margin-right: 4px;
    }
    .child-count {
        font-size: 0.68rem;
        font-weight: 700;
        color: #64748b;
        background: #e2e8f0;
        padding: 0 7px;
        border-radius: 10px;
        pointer-events: none;
    }

    .tree-icon {
        font-size: 0.95rem;
        line-height: 1;
        pointer-events: none;
    }
    .tree-label {
        flex: 1;
        letter-spacing: -0.01em;
        pointer-events: none;
    }
    .bold {
        font-weight: 700;
        color: #0f172a;
    }

    /* 커스텀 체크박스 (클릭 영역은 상위 .tree-node가 담당) */
    .custom-checkbox {
        position: relative;
        flex-shrink: 0;
        width: 15px;
        height: 15px;
        background-color: #ffffff;
        border: 1.5px solid #94a3b8;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        pointer-events: none;
    }
    .tree-node:hover .custom-checkbox {
        border-color: #3b82f6;
    }
    .custom-checkbox.checked,
    .custom-checkbox.indeterminate {
        background-color: #2563eb;
        border-color: #2563eb;
    }
    .custom-checkbox.checked::after {
        content: "";
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
        margin-bottom: 2px;
    }
    .custom-checkbox.indeterminate::after {
        content: "";
        width: 8px;
        height: 2px;
        background-color: white;
        border-radius: 1px;
    }

    /* 푸터 스타일 */
    .modal-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 20px;
        border-top: 1px solid #e2e8f0;
        background: #f8fafc;
    }
    .footer-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .footer-right {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .selection-badge {
        font-size: 0.8rem;
        color: #475569;
    }
    .selection-badge strong {
        color: #2563eb;
        font-weight: 800;
    }

    .btn {
        padding: 7px 16px;
        border-radius: 6px;
        font-size: 0.84rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s;
        line-height: 1.3;
    }
    .btn-secondary {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        color: #334155;
    }
    .btn-secondary:hover {
        background: #f1f5f9;
        border-color: #94a3b8;
    }
    .btn-primary {
        background: #2563eb;
        border: 1px solid #1d4ed8;
        color: white;
        box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
    }
    .btn-primary:hover:not(:disabled) {
        background: #1d4ed8;
    }
    .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
</style>
